import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Box, Drawer, IconButton, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ThemedSvg } from "../../components/UIToolkit/ThemedSvg";
import { type HospitalDataContextType, useHospitalData } from "../../contexts/HospitalDataProvider";
import { HOSPITAL_DEMO, INK, NEUTRAL } from "../../theme/tokens";
import { findScheduleSlot } from "../../utils/departmentWeeks";
import type { ScheduledProcedure } from "../../utils/models";
import { useShiftInsightsWeek } from "../chartContainer/useShiftInsightsWeek";
import ProceduresViewSelector, { type ViewType, ViewTypeOptions } from "../proceduresViewSelector/ProceduresViewSelector";
import Style from "./PatientDrawer.module.scss";

// Difficulty indicators for the patient drawer's day staffing badge.
// Functional/categorical — they encode load level, not brand.
const DIFFICULTY = {
	HIGH: "#FFA96A",
	LOW: "#5EB8F9",
	NORMAL: "#727379",
} as const;

function addMinutesToDate(date: DateTime, minutes: number): DateTime {
	return date.plus({ minutes: minutes });
}

export const presentRangeScore = (low: number | undefined, high: number | undefined) => {
	if (!high) {
		return low;
	}

	return `${low}-${high}`;
};

function calculateAge(birthDate: DateTime | null): number | string {
	if (!birthDate) return "";
	return Math.round(DateTime.now().diff(birthDate, "year").years);
}

function formatDateTime(i18n: any, date: DateTime): string {
	if (!date) return "";
	if (i18n.language === "en") {
		return date.toFormat("h:mm a");
	}
	return date.toFormat("HH:mm");
}

const sortProceduresByStartTime = (procedures: ScheduledProcedure[]) => {
	return procedures.sort((a, b) => a.activityDate.valueOf() - b.activityDate.valueOf());
};

const getTimeRange = (i18n: any, procedures: ScheduledProcedure[]) => {
	if (procedures.length === 0) return "";

	const startTimes = procedures.map((p) => p.activityDate.valueOf());
	const endTimes = procedures.map((p) => p.activityDate.valueOf() + p.duration * 60000);

	const earliestStart = DateTime.fromMillis(Math.min(...startTimes));
	const latestEnd = DateTime.fromMillis(Math.max(...endTimes));

	return i18n.dir() === "rtl"
		? `${formatDateTime(i18n, latestEnd)} - ${formatDateTime(i18n, earliestStart)}`
		: `${formatDateTime(i18n, earliestStart)} - ${formatDateTime(i18n, latestEnd)}`;
};

interface ScheduleProcedureCardParams {
	patient: ScheduledProcedure;
	viewType: ViewType;
}

const ScheduleProcedureCard = ({ patient, viewType }: ScheduleProcedureCardParams) => {
	const { t, i18n } = useTranslation();

	function getDuration(total_mins: number): string {
		const hours = Math.floor(total_mins / 60);
		const minutes = Math.floor(total_mins % 60);

		if (hours === 0) {
			return `${minutes} ${t("min")}`;
		}
		return `${hours}:${minutes.toString().padStart(2, "0")} ${t("hr")}`;
	}

	// Time range text, per view type (procedure time vs. admission window).
	const timeText =
		viewType === "By Admission"
			? i18n.dir() === "rtl"
				? `${formatDateTime(patient, patient.departmentArrivalTimeMax)} - ${formatDateTime(patient, patient.departmentArrivalTimeMin)}`
				: `${formatDateTime(patient, patient.departmentArrivalTimeMin)} - ${formatDateTime(patient, patient.departmentArrivalTimeMax)}`
			: i18n.dir() === "rtl"
				? `${formatDateTime(patient, addMinutesToDate(patient.activityDate, patient.duration))} - ${formatDateTime(patient, patient.activityDate)}`
				: `${formatDateTime(patient, patient.activityDate)} - ${formatDateTime(patient, addMinutesToDate(patient.activityDate, patient.duration))}`;

	// One flat row per surgery: name · surgeon · time · patient — no card, colored circle, or icons.
	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: "8px",
				padding: "10px 4px",
				borderBottom: `1px solid ${NEUTRAL[200]}`,
				fontSize: "14px",
				color: NEUTRAL[800],
			}}
		>
			<span style={{ fontWeight: "bold", flex: "0 0 34%" }}>{patient.activities.join(", ")}</span>
			<span style={{ flex: "0 0 20%", color: INK.MUTED }}>{patient.surgeon}</span>
			<span style={{ flex: "0 0 22%", color: INK.MUTED }}>
				{timeText}
				{viewType === "By Admission" ? "" : `, ${getDuration(patient.duration)}`}
			</span>
			<span style={{ flex: 1, textAlign: "end" }}>
				{patient.patientName} | {t(patient.gender)} | {calculateAge(patient.birthDate)}
			</span>
		</div>
	);
};

interface GroupedProcedureParams {
	propVal: string;
	procedures: ScheduledProcedure[];
	viewType: ViewType;
}

const GroupedProcedure = ({ propVal, procedures, viewType }: GroupedProcedureParams) => {
	const { t, i18n } = useTranslation();

	return (
		<Accordion>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography>
					<b>{propVal}</b>
					{procedures.length === 1 && i18n.dir() === "rtl" ? (
						<>
							{" "}
							{t("procedure")} {procedures.length}
						</>
					) : (
						<>
							{" "}
							{procedures.length} {procedures.length === 1 ? t("procedure") : t("procedures")}
						</>
					)}
					<ThemedSvg src="/clock.svg" alt="clock" size={20} color="var(--inhouse-primary)" />

					<span style={{ marginLeft: "10px", fontSize: "0.9em", color: INK.MUTED }}>{getTimeRange(i18n, procedures)}</span>
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				{sortProceduresByStartTime(procedures).map((patient, patientIndex) => (
					<div key={patientIndex}>
						<ScheduleProcedureCard patient={patient} viewType={viewType} />
					</div>
				))}
			</AccordionDetails>
		</Accordion>
	);
};

type PatientDrawerProps = {
	drawerOpen: boolean;
	setDrawerOpen: (open: boolean) => void;
	/** The clicked chart day and shift; both absent until a day is clicked. */
	date: DateTime | undefined;
	shiftType: string | undefined;
};

export const PatientDrawer = ({ drawerOpen, setDrawerOpen, date, shiftType }: PatientDrawerProps) => {
	const { hospitalData, isHospitalView }: HospitalDataContextType = useHospitalData();
	const { weeks: insightWeeks } = useShiftInsightsWeek();
	const [_windowWidth, setWindowWidth] = useState(window.innerWidth);
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const brandWash = alpha(theme.palette.primary.main, 0.2);
	const getRecomndedStaff = (shift: any) => {
		let dataString = shift ? shift.staffNo.toString() : "";
		if (shift?.isPlusActive) dataString += "+";
		else if (shift?.isMinusActive) dataString += "-";
		return dataString;
	};
	const [viewType, setViewType] = useState<ViewType>((localStorage.getItem("proceduresView") ?? ViewTypeOptions[0]) as ViewType);
	const handleViewTypeChanged = (newViewType: ViewType) => {
		setViewType(newViewType);
	};
	// Shift filter: All merges both shifts' procedures; Day/Night filter to one shift.
	const [shiftFilter, setShiftFilter] = useState<"All" | "Day" | "Night">("All");

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);
	const slot = date && shiftType ? findScheduleSlot(insightWeeks, date, shiftType) : undefined;
	const currDay = slot ? insightWeeks[slot.weekIndex].weeklyShifts[slot.shiftIndex].days[slot.dayIndex] : undefined;
	if (!currDay) {
		return <></>;
	}
	// Procedures for the clicked date, by shift type. The new demo uses Day/Night; the old one used
	// Morning/Evening/Night, so Day collects all the daytime ones.
	const proceduresByShift = hospitalData.procedures?.[currDay.date.toFormat("yyyy-MM-dd")] ?? {};
	const DAY_SHIFT_KEYS = ["Day", "Morning", "Evening", "Afternoon"];
	const NIGHT_SHIFT_KEYS = ["Night"];
	// Show as many procedures as the Admissions number in the header above.
	// The three tabs split that one number, so Day + Night = All.
	// There are usually more procedures than we need, and we drop the extra ones.
	const admissionsTarget = currDay?.shiftEvents?.numberAdmissionsHigh ?? 0;
	const dayProcedures = DAY_SHIFT_KEYS.flatMap((key) => proceduresByShift[key] ?? []);
	const nightProcedures = NIGHT_SHIFT_KEYS.flatMap((key) => proceduresByShift[key] ?? []);
	// Night gets a third, Day gets the rest. Without this Day would take everything and the
	// Night tab would always be empty.
	const NIGHT_PROCEDURE_SHARE = 1 / 3;
	const nightShare = Math.min(nightProcedures.length, Math.floor(admissionsTarget * NIGHT_PROCEDURE_SHARE));
	const dayShare = Math.min(dayProcedures.length, admissionsTarget - nightShare);
	const patientsData =
		shiftFilter === "Day"
			? dayProcedures.slice(0, dayShare)
			: shiftFilter === "Night"
				? nightProcedures.slice(0, nightShare)
				: [...dayProcedures.slice(0, dayShare), ...nightProcedures.slice(0, nightShare)];

	const formatScore = (score: number | undefined): string => {
		if (score === undefined) return "";

		const fixedScore = score.toFixed(1);
		return Number.parseFloat(fixedScore) % 1 === 0 ? Number.parseInt(fixedScore, 10).toString() : fixedScore;
	};

	const groupProcedures = (procedures: ScheduledProcedure[], propName: keyof ScheduledProcedure): Record<string, ScheduledProcedure[]> => {
		return procedures.reduce(
			(acc, procedure) => {
				const key = String(procedure[propName]);
				if (!acc[key]) {
					acc[key] = [];
				}
				acc[key].push(procedure);
				return acc;
			},
			{} as Record<string, ScheduledProcedure[]>,
		);
	};

	const getEarliestStartTime = (procedures: ScheduledProcedure[]) => {
		return Math.min(...procedures.map((p) => p.activityDate.valueOf()));
	};

	const getSortedProcedures = (propName: keyof ScheduledProcedure) => {
		return Object.entries(groupProcedures(patientsData, propName)).sort(
			([, proceduresA], [, proceduresB]) => getEarliestStartTime(proceduresA) - getEarliestStartTime(proceduresB),
		);
	};

	return (
		<Drawer sx={{ zIndex: 999999999999 }} anchor={i18n.dir() === "rtl" ? "left" : "right"} open={drawerOpen} onClose={() => setDrawerOpen(false)}>
			<div style={{ width: "550px", maxWidth: "calc(100vw - 30px)" }}>
				<div className={Style.fixedSection}>
					<IconButton
						onClick={() => {
							setDrawerOpen(false);
						}}
					>
						{i18n.dir() === "rtl" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
					</IconButton>
					<div className={Style.fixedDiv}>
						{/* Title on its own line, with the view tabs directly below it. */}
						<h2 style={{ margin: "0 0 8px 0", fontWeight: "bold", color: NEUTRAL[800] }}>{t("Surgery Schedule")}</h2>
						<ProceduresViewSelector onViewChanged={handleViewTypeChanged} />
						<Box
							sx={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "center",
								color: INK.SECONDARY,
								marginTop: "12px",
							}}
						>
							<span style={{ fontWeight: "400" }}>{currDay?.date.toFormat("MMMM d, cccc")}</span>
							{/* All / Day / Night filter replaces the old < Day > arrows. */}
							<ToggleButtonGroup
								value={shiftFilter}
								exclusive
								size="small"
								onChange={(_e, next: "All" | "Day" | "Night" | null) => next && setShiftFilter(next)}
								aria-label="shift filter"
							>
								<ToggleButton value="All" sx={{ textTransform: "none", padding: "2px 12px" }}>
									{t("All")}
								</ToggleButton>
								<ToggleButton value="Day" sx={{ textTransform: "none", padding: "2px 12px" }}>
									{t("Day")}
								</ToggleButton>
								<ToggleButton value="Night" sx={{ textTransform: "none", padding: "2px 12px" }}>
									{t("Night")}
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>
					</div>

					<div
						className={Style.fixedDiv}
						style={{
							backgroundColor: brandWash,
							padding: "12px",
							fontSize: "18px",
							color: NEUTRAL[800],
							borderRadius: "4px",
							marginBottom: "10px",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<div
							style={{
								// Staffing badge color by difficulty. Demo (inhouse2 + Hospital) uses the demo palette
								// to match the chart's red/blue nodes; regular depts keep the original DIFFICULTY colors.
								backgroundColor: (() => {
									const palette = isHospitalView
										? { high: HOSPITAL_DEMO.NODE_HIGH, low: HOSPITAL_DEMO.NODE_LOW, normal: HOSPITAL_DEMO.NODE_MEDIUM }
										: { high: DIFFICULTY.HIGH, low: DIFFICULTY.LOW, normal: DIFFICULTY.NORMAL };
									return currDay?.staffDifficulty === "high" ? palette.high : currDay?.staffDifficulty === "low" ? palette.low : palette.normal;
								})(),
								borderRadius: "50%",
								padding: "10px",
								width: "50px",
								height: "50px",
								color: "white",
								fontWeight: "bold",
								marginBottom: "10px",
								textAlign: "center",
							}}
						>
							{getRecomndedStaff(currDay)}
						</div>
						<div className={Style.dataContainer}>
							<div className={Style.dataRow}>
								<div className={Style.dataCell}>{t("Patients")}</div>
								<div className={Style.dataCell}>{t("Patient Score")}</div>
								<div className={Style.dataCell}>{t("Admissions")}</div>
								<div className={Style.dataCell}>{t("Discharges")}</div>
							</div>
							<div className={Style.dataRow}>
								<div className={Style.dataCell}>{presentRangeScore(currDay?.census_low, currDay?.census_high)}</div>
								<div className={Style.dataCell}>{formatScore(currDay?.score)}</div>
								<div className={Style.dataCell}>{presentRangeScore(currDay?.shiftEvents?.numberAdmissionsLow, currDay?.shiftEvents?.numberAdmissionsHigh)}</div>
								<div className={Style.dataCell}>{presentRangeScore(currDay?.shiftEvents?.numberDischargesLow, currDay?.shiftEvents?.numberDischargesHigh)}</div>
							</div>
						</div>
					</div>
				</div>

				<div className={Style.scrollableDiv} style={{ padding: "5px" }}>
					<div
						style={{
							marginBottom: "10px",
							fontSize: "16px",
							fontWeight: "bold",
							color: NEUTRAL[800],
						}}
					>
						{viewType === "By Surgeon" || viewType === "By Procedure" || viewType === "Procedure Time" ? (
							<>
								{t("Number of Procedures")}: {patientsData.length}
							</>
						) : (
							<>
								{t("Number of Expected Admissions")}: {patientsData.length}
							</>
						)}
					</div>
					{viewType === "By Surgeon"
						? getSortedProcedures("surgeon").map(([surgeon, procedures], index) => (
								<GroupedProcedure key={index} propVal={surgeon} procedures={procedures} viewType={viewType} />
							))
						: viewType === "By Procedure"
							? getSortedProcedures("activity").map(([surgeon, procedures], index) => (
									<GroupedProcedure key={index} propVal={surgeon} procedures={procedures} viewType={viewType} />
								))
							: sortProceduresByStartTime(patientsData).map((patient, index) => (
									<div key={index}>
										<ScheduleProcedureCard patient={patient} viewType={viewType} />
									</div>
								))}
				</div>
			</div>
		</Drawer>
	);
};
